import React, { useState, useEffect } from "react";
import { Form, Input, InputNumber, Button, Space } from "antd";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../shared/utils/Util";
import { fetchT } from "../../shared/utils/TypedUtil";
import { useNotification } from "../../shared/hooks";
import {
  WorkstationSchema,
  CreateWorkstationSchemaRequest,
  UpdateWorkstationSchemaRequest,
  SetWorkstationPrepWeightRequest,
  MAX_PREP_WEIGHT,
} from "./types";

type WorkstationSchemaFormProps = {
  restaurantId: string;
  onSuccess: () => void;
  editingSchema?: WorkstationSchema | null;
  onCancelEdit?: () => void;
};

type FormValues = {
  name: string;
  prep_weight: number;
};

export const WorkstationSchemaForm: React.FC<WorkstationSchemaFormProps> = ({
  restaurantId,
  onSuccess,
  editingSchema,
  onCancelEdit,
}) => {
  const { t } = useTranslation();
  const { createSuccess, updateSuccess, notifyError } = useNotification();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingSchema) {
      form.setFieldsValue({
        name: editingSchema.name,
        prep_weight: editingSchema.prep_weight ?? 0,
      });
    } else {
      form.resetFields();
    }
  }, [editingSchema, form]);

  // Prep weight lives behind a dedicated endpoint (so name-only updates
  // cannot clobber it) and is saved in a follow-up request. The schema
  // itself is already persisted by then, so the form is reset and the list
  // refreshed even when the follow-up fails - otherwise the UI would look
  // like the save failed and a retry would create a duplicate schema.
  const finishSave = (
    notifySaved: (entity: string) => void,
    schemaId: string,
    prepWeightChanged: boolean,
    prepWeight: number
  ) => {
    const finish = () => {
      form.resetFields();
      onSuccess();
      setLoading(false);
    };

    if (!prepWeightChanged) {
      notifySaved(t("workstation_schema"));
      finish();
      return;
    }

    const request: SetWorkstationPrepWeightRequest = { prep_weight: prepWeight };
    fetchT<Record<string, never>>(
      "PUT",
      `${API_URL}/v1/r/${restaurantId}/workstations/${schemaId}/prep_weight`,
      request,
      () => {
        notifySaved(t("workstation_schema"));
        finish();
      },
      (err) => {
        notifyError(t("failed_to_update_prep_weight"), err);
        finish();
      }
    );
  };

  const onFinish = (values: FormValues) => {
    setLoading(true);

    if (editingSchema) {
      const request: UpdateWorkstationSchemaRequest = {
        name: values.name,
      };

      fetchT<{ id: string }>(
        "PUT",
        `${API_URL}/v1/r/${restaurantId}/admin/workstation_schemas/${editingSchema.id}`,
        request,
        () => {
          finishSave(
            updateSuccess,
            editingSchema.id,
            values.prep_weight !== (editingSchema.prep_weight ?? 0),
            values.prep_weight
          );
        },
        (err) => {
          notifyError(t("failed_to_update_workstation_schema"), err);
          setLoading(false);
        }
      );
    } else {
      const request: CreateWorkstationSchemaRequest = {
        name: values.name,
      };

      fetchT<{ id: string }>(
        "POST",
        `${API_URL}/v1/r/${restaurantId}/admin/workstation_schemas`,
        request,
        (result) => {
          finishSave(createSuccess, result.id, values.prep_weight > 0, values.prep_weight);
        },
        (err) => {
          notifyError(t("failed_to_create_workstation_schema"), err);
          setLoading(false);
        }
      );
    }
  };

  const handleCancel = () => {
    form.resetFields();
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ prep_weight: 0 }}>
      <Form.Item
        name="name"
        label={t("name")}
        rules={[{ required: true, message: t("name_is_required") }]}
      >
        <Input placeholder={t("enter_workstation_name")} />
      </Form.Item>

      <Form.Item
        name="prep_weight"
        label={t("prep_weight")}
        tooltip={t("prep_weight_description")}
        rules={[
          {
            required: true,
            type: "number",
            min: 0,
            max: MAX_PREP_WEIGHT,
            message: t("prep_weight_range", { max: MAX_PREP_WEIGHT }),
          },
        ]}
      >
        <InputNumber
          min={0}
          max={MAX_PREP_WEIGHT}
          step={1}
          precision={0}
          addonAfter={t("prep_weight_unit")}
          style={{ width: "100%", maxWidth: 320 }}
        />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {editingSchema ? t("save") : t("create")}
          </Button>
          <Button onClick={handleCancel}>{editingSchema ? t("cancel") : t("reset")}</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
